<template>
    <div>
        <common-modal v-if="isOpen" height="small" @close="$emit('close')">
            <common-content-spacer height="medium">
                <common-loader :isProcessing="isFetching || isLoading"></common-loader>
                <div v-if="latestReferral && !isFetching">
                    <!-- Header -->
                    <div class="referral-tracking-modal__header">
                        <h2>{{ $t('pages.referrals.index.referral_modal.head') }}</h2>
                    </div>

                    <!-- Patient Info -->
                    <div class="referral-tracking-modal__section">
                        <p class="section-title">{{ $t('pages.eprescribe.eprescribe.steps.step_3') }}</p>
                        <div class="card-refferal">
                            <div>
                                <span class="label">{{ $t('forms.patient_profile.fields.pharmacy.name.lable') }}</span>
                                <p class="birth-date">{{ getUserFullName(latestReferral?.patient) }}</p>
                            </div>
                            <div>
                                <span class="label">{{ $t('forms.patient.patient_information.fields.date.label') }}</span>
                                <p class="birth-date">{{ getPatientDateOfBirth(latestReferral?.patient?.dateOfBirth) }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Provider -->
                    <div v-if="showProviderSection() && providerSectionType" class="referral-tracking-modal__section">
                        <p class="section-title">
                            {{ sectionTitle }}
                        </p>

                        <div class="card-provider">
                            <div class="user-avatar">
                                <common-user-avatar size="" :user="providerUser"></common-user-avatar>
                            </div>

                            <div>
                                <p class="name">{{ getUserName(latestReferral) }}</p>
                                <p class="sub">{{ getUserSpeciality(specialityUser) }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Status -->
                    <div class="referral-tracking-modal__section">
                        <p class="section-title">{{ $t('modals.patient.medical.head') }}</p>
                        <div class="card-refferal">
                            <div>
                                <p class="label">{{ $t('pages.referrals.index.referrals_made.stage') }}</p>
                                <span :class="getStageClass(latestReferral?.stageInfo?.stage)">
                                    {{ formatStage(latestReferral?.stageInfo?.stage) }}
                                </span>
                            </div>
                            <div>
                                <p class="label">{{ $t('pages.referrals.index.referrals_made.typer') }}</p>
                                <span class="referral-tracking-modal__referrals" :class="referralTypeClass(sourceType)">
                                    {{ getReferralType(sourceType) }}
                                </span>
                            </div>
                            <div>
                                <p class="label">{{ $t('pages.referrals.index.referral_modal.date') }}</p>
                                <p class="referral-tracking-modal__month-date">{{ getCreatedDate(latestReferral) }}</p>
                            </div>
                        </div>
                    </div>

                    <hr />

                    <!-- Progress -->
                    <div class="referral-tracking-modal__section">
                        <p class="section-title">{{ $t('pages.referrals.index.referral_modal.timeline') }}</p>

                        <div class="referral-tracking-modal__timeline">
                            <!-- step 1 -->
                            <div class="item done">
                                <div class="dot">
                                    <span class="check">✓</span>
                                </div>
                                <div class="referral-tracking-modal__timeline-referral">
                                    <p>{{ $t('pages.referrals.show.details.referral_received') }}</p>
                                    <span>{{ createdAt }}</span>
                                </div>
                            </div>
                            <div v-if="proposeSlotsCondition">
                                <div :class="['item', referralStatusClass]">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="latestReferral.status === ReferralStatusEnum.SCHEDULED" class="check">
                                                ✓
                                            </span>
                                            <span v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE" class="cross">
                                                X
                                            </span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p v-if="latestReferral.status === ReferralStatusEnum.CREATED">
                                                {{ $t('pages.referrals.pool.accept_and_propose_time') }}
                                            </p>
                                            <common-buttons
                                                v-if="latestReferral.status === ReferralStatusEnum.CREATED"
                                                align="left"
                                            >
                                                <common-button
                                                    :label="$t('pages.referrals.notes.add.buttons.yes')"
                                                    type="primary"
                                                    size="tiny"
                                                    @click="processReferral()"
                                                ></common-button>
                                                <common-button
                                                    :label="$t('pages.referrals.show.buttons.decline')"
                                                    type="secondary"
                                                    size="tiny"
                                                    @click="declineReferral()"
                                                ></common-button>
                                            </common-buttons>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                                {{ $t('pages.referrals.tracking.status_description.CONFIRMED') }}
                                            </p>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE">
                                                {{ $t('pages.referrals.tracking.status_description.INACTIVE_NEW') }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <div v-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                            <a
                                                @click="
                                                    emitEvent({
                                                        name: 'open-direct-referral',
                                                        payload: latestReferral.referralId,
                                                    })
                                                "
                                            >
                                                {{ $t('pages.referrals.show.view_details.buttons.view_details') }}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="acceptSlotsConditon">
                                <div :class="['item', referralStatusClass]">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="latestReferral.status === ReferralStatusEnum.SCHEDULED" class="check">
                                                ✓
                                            </span>
                                            <span v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE" class="cross">
                                                X
                                            </span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p v-if="latestReferral.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED">
                                                {{ $t('pages.referrals.pool.choose_provider_slots') }}
                                            </p>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                                {{ $t('pages.referrals.tracking.status_description.CONFIRMED') }}
                                            </p>
                                            <p v-else-if="latestReferral.status === ReferralStatusEnum.INACTIVE">
                                                {{ $t('pages.referrals.tracking.status_description.INACTIVE_NEW') }}
                                            </p>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <common-buttons v-if="latestReferral.status === ReferralStatusEnum.TIME_SLOTS_PROPOSED">
                                            <router-link :to="{ path: `/referrals/pool/${type}/${latestReferral.id}` }">
                                                <common-button
                                                    size="tiny"
                                                    :label="$t('common.microcopy.continue')"
                                                    type="primary"
                                                ></common-button>
                                            </router-link>
                                        </common-buttons>
                                        <div v-else-if="latestReferral.status === ReferralStatusEnum.SCHEDULED">
                                            <a
                                                @click="
                                                    emitEvent({
                                                        name: 'open-direct-referral',
                                                        payload: latestReferral.referralId,
                                                    })
                                                "
                                            >
                                                {{ $t('pages.referrals.show.view_details.buttons.view_details') }}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div v-else-if="showTimelineCondition">
                                <!-- step 2 -->
                                <div :class="step2Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="isApproved" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p v-if="isApproved">
                                                {{ $t('pages.referrals.tracking.status_description.CONFIRMED') }}
                                            </p>
                                            <p v-else>{{ notApprovedText }}</p>

                                            <template v-if="isApproved">
                                                <span>{{ approvedAt }}</span>
                                            </template>
                                            <p
                                                v-if="scheduledForDate && scheduledForTime"
                                                class="referral-tracking-modal__scheduled-info"
                                            >
                                                {{ $t('pages.referrals.index.headings.scheduled_for') }}:
                                                <b>{{ scheduledForDate }}</b>
                                                {{ $t('pages.referrals.show.view_details.at') }}
                                                <b>{{ scheduledForTime }}</b>
                                            </p>
                                        </div>
                                    </div>
                                    <div v-if="shouldShowRightContent" class="right-content">
                                        <a @click="openReferralInfoModal()">
                                            {{ $t('pages.referrals.show.view_details.buttons.view_details') }}
                                        </a>
                                    </div>
                                </div>
                                <!-- step 3 -->
                                <div :class="['item', insuranceStatus]">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="insuranceStatus === 'done'" class="check">✓</span>

                                            <span v-else-if="insuranceStatus === 'declined'" class="cross">X</span>

                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ insuranceTimelineText }}</p>

                                            <template v-if="insuranceStatus === 'initial'">
                                                <p class="referral-tracking-modal__scheduled-info">
                                                    {{ latestReferral.insurancePlan.name }}
                                                </p>
                                            </template>

                                            <template v-else-if="insuranceStatus === 'done'">
                                                <span>{{ insuranceAuthObtainedAt }}</span>
                                                <p class="referral-tracking-modal__scheduled-info">
                                                    {{ latestReferral.insurancePlan.name }}
                                                    <b v-if="latestReferral.insuranceAuthNumber">
                                                        #{{ latestReferral.insuranceAuthNumber }}
                                                    </b>
                                                </p>
                                            </template>

                                            <template v-else>
                                                <span>
                                                    {{ $t('pages.referrals.show.details.insurance_plan') }}:
                                                    {{ latestReferral.insurancePlan.name }}
                                                </span>
                                            </template>
                                        </div>
                                    </div>

                                    <div v-if="showInsuranceUpdateButton" class="right-content">
                                        <common-button
                                            type="primary"
                                            size="tiny"
                                            :label="$t('pages.referrals.show.buttons.update_status')"
                                            @click="openInsuranceAuthorizationModal"
                                        ></common-button>
                                    </div>
                                </div>
                                <!-- step 4 -->
                                <div :class="step4Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="patientSeenStatus === 'done'" class="check">✓</span>

                                            <span v-else-if="patientSeenStatus === 'error'" class="cross">X</span>

                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ patientSeenTitle }}</p>

                                            <span>{{ scheduledForDate }}</span>

                                            <p v-if="showPatientSeenPending" class="referral-tracking-modal__scheduled-info">
                                                {{ $t('pages.referrals.show.details.at') }}
                                                <b>{{ scheduledForTime }}</b>
                                            </p>
                                        </div>
                                    </div>

                                    <div class="right-content">
                                        <template v-if="showUpdateStatus">
                                            <common-button
                                                type="primary"
                                                size="tiny"
                                                :label="$t('pages.referrals.show.buttons.update_status')"
                                                @click="openPatientSeenModal"
                                            ></common-button>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 5 -->
                                <div :class="step5Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="referralStatus === 'done'" class="check">✓</span>

                                            <span
                                                v-else-if="referralStatus === 'pending'"
                                                class="referral-tracking-modal__checks"
                                            ></span>

                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>
                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>
                                                {{ referralStatusTitle }}
                                            </p>
                                            <common-buttons v-if="isConsultationNotesPendingReceived" align="left">
                                                <common-button
                                                    :label="$t('pages.referrals.notes.add.buttons.yes')"
                                                    type="primary"
                                                    size="tiny"
                                                    @click="reportAlreadyAdded"
                                                ></common-button>
                                                <common-button
                                                    :label="$t('pages.referrals.notes.add.buttons.no')"
                                                    type="secondary"
                                                    size="tiny"
                                                    @click="addNotesClicked"
                                                ></common-button>
                                            </common-buttons>
                                            <span v-if="latestReferral.status === ReferralStatusEnum.COMPLETED">
                                                {{ notesUploadedDate }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <template v-if="isStatusCompletedAndUserNotOther">
                                            <router-link :to="referralRoute">
                                                <common-button
                                                    :label="$t('pages.referrals.show.view_details.view_report')"
                                                    type="primary"
                                                    size="tiny"
                                                ></common-button>
                                            </router-link>
                                        </template>
                                        <template v-else-if="isConsultationNotesPendingMade">
                                            <template v-if="!latestReferral.notesRequestDateTime && !reportNewRequestDateTime">
                                                <common-button
                                                    :label="$t('pages.referrals.show.view_details.buttons.request_report')"
                                                    type="primary"
                                                    size="tiny"
                                                    @click="openRequestReportModal"
                                                ></common-button>
                                            </template>

                                            <template v-else>
                                                <p>{{ $t('pages.referrals.show.details.report_requested') }}</p>
                                                <p>{{ referralReportRequestedDateTime }}</p>
                                                <a @click="openRequestReportModal">
                                                    {{ $t('pages.referrals.show.details.solicit_again') }}
                                                </a>
                                            </template>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 6 -->
                                <div v-if="type == ReferralPathTypeEnum.RECEIVED" :class="step6Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="peerRatingStatus === 'done'" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>
                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>
                                                {{
                                                    isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings
                                                        ? 'Rating received'
                                                        : $t('pages.referrals.show.view_details.rating_not_available')
                                                }}
                                            </p>
                                            <span
                                                v-if="
                                                    isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings
                                                "
                                            >
                                                {{ peerRatingDate }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="right-content">
                                        <template
                                            v-if="isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings"
                                        >
                                            <common-rating-stars
                                                v-if="latestReferral.fromUser"
                                                :rating="peerRating"
                                                :enableViewDetails="true"
                                                @viewDetailsClicked="viewPeerReviewDetailsClicked"
                                            ></common-rating-stars>
                                            <ratings-summary-modal
                                                v-if="showPeerReviewModal"
                                                :ratings="ratingsForPeerModal"
                                                :summaryText="summaryTextForPeerReviewModal"
                                                :summarySubText="summarySubTextForPeerReviewModal"
                                                :maxGrade="5"
                                                :highlightedRatingKey="'overallSatisfaction'"
                                                :numberedList="false"
                                                @close="viewPeerReviewDetailsModalClosed"
                                            ></ratings-summary-modal>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 6 -->
                                <div v-if="type === ReferralPathTypeEnum.MADE" :class="step6Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="peerRatingStatus === 'done'" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ patientRatingText }}</p>

                                            <span v-if="isReferralCompleted(latestReferral) && latestReferral.patientReview">
                                                {{ patientRatingDate }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="right-content">
                                        <template v-if="isReferralCompleted(latestReferral) && latestReferral.patientReview">
                                            <common-rating-stars
                                                :rating="patientRating"
                                                :enableViewDetails="true"
                                                @viewDetailsClicked="viewPatientReviewDetailsClicked"
                                            ></common-rating-stars>

                                            <ratings-summary-modal
                                                v-if="showPatientReviewModal"
                                                :ratings="ratingsForPatientModal"
                                                :summaryText="summaryTextForPatientReviewModal"
                                                :maxGrade="5"
                                                :highlightedRatingKey="'overallSatisfaction'"
                                                :numberedList="false"
                                                @close="viewPatientReviewDetailsModalClosed"
                                            ></ratings-summary-modal>
                                        </template>
                                    </div>
                                </div>
                                <!-- step 7 -->
                                <div v-if="type === ReferralPathTypeEnum.MADE" :class="step7Class">
                                    <div class="left-content">
                                        <div class="dot">
                                            <span v-if="step7Status === 'done'" class="check">✓</span>
                                            <span v-else class="referral-tracking-modal__checks"></span>
                                        </div>

                                        <div class="referral-tracking-modal__timeline-referral">
                                            <p>{{ physicianRatingText }}</p>

                                            <span
                                                v-if="
                                                    isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings
                                                "
                                            >
                                                {{ peerRatingDate }}
                                            </span>
                                        </div>
                                    </div>

                                    <div class="right-content">
                                        <template
                                            v-if="isReferralCompleted(latestReferral) && latestReferral.physicianReview?.ratings"
                                        >
                                            <common-rating-stars
                                                :rating="peerRating"
                                                :enableViewDetails="true"
                                                @viewDetailsClicked="viewPeerReviewDetailsClicked"
                                            ></common-rating-stars>
                                            <ratings-summary-modal
                                                v-if="showPeerReviewModal"
                                                :ratings="ratingsForPeerModal"
                                                :summaryText="summaryTextForPhysicianReviewModal"
                                                :maxGrade="5"
                                                :highlightedRatingKey="'overallSatisfaction'"
                                                :numberedList="false"
                                                @close="viewPeerReviewDetailsModalClosed"
                                            ></ratings-summary-modal>
                                        </template>

                                        <template v-else-if="showPendingReviewBlock">
                                            <common-button
                                                :label="$t('pages.referrals.show.buttons.rate_referral')"
                                                type="primary"
                                                size="tiny"
                                                @click="rateReferralClicked"
                                            ></common-button>
                                        </template>

                                        <template v-else>
                                            <span></span>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="referral-tracking-modal__activity">
                            <span>{{ $t('pages.referrals.index.referral_modal.last_acti') }}</span>
                            <span class="bold">5 hours ago</span>
                        </div>
                        <p class="referral-tracking-modal__month-date">
                            {{ $t('pages.referrals.index.referral_modal.needed') }}
                        </p>
                        <div class="referral-tracking-modal__needed">
                            <p>{{ latestReferral?.stageInfo?.nextAction }}</p>
                        </div>
                    </div>
                </div>
            </common-content-spacer>
        </common-modal>
        <referrals-patient-details-modal
            v-if="showPatientDetailsModal"
            :referral="latestReferral"
            @close="showPatientDetailsModal = false"
        ></referrals-patient-details-modal>
        <pending-insurance-authorization-modal
            v-if="showInsuranceModal"
            :referral="latestReferral"
            @close="closeInsuranceAuthorizationModal()"
            @close-and-show-info="
                closeInsuranceAuthorizationModal()
                openInsuranceAuthorizationInfoModal()
            "
            @declined="insuranceAuthDenied"
            @obtained="insuranceAuthObtained"
        ></pending-insurance-authorization-modal>
        <referrals-is-patient-seen-modal
            v-if="showPatientSeenModal"
            :referral="latestReferral"
            @patient-seen="patientSeen"
            @skip-all="showPatientSeenModal = false"
            @close="showPatientSeenModal = false"
        ></referrals-is-patient-seen-modal>
        <request-report-modal
            :isOpen="showRequestReportModal"
            :referral="latestReferral"
            @close="closeRequestReportModal"
            @sent="requestReportSent"
        ></request-report-modal>
        <is-report-uploaded-modal
            :isOpen="isReportUploadedModal"
            :referral="latestReferral"
            @refresh-details="refreshReferralDetails"
            @close="isReportUploadedModal = false"
        ></is-report-uploaded-modal>
        <available-locations-modal
            v-if="showAvailableLocationsModal"
            :referral="latestReferral"
            :locations="locations"
            @close="closeAvailableLocationsModal"
        ></available-locations-modal>
        <subscribe-to-premium-modal
            v-if="showPremiumSubscriptionModal"
            :isOpen="showPremiumSubscriptionModal"
            @close="showPremiumSubscriptionModal = false"
        ></subscribe-to-premium-modal>
    </div>
</template>

<script>
    import StagesEnum from '@/enums/StagesEnum'
    import ReferralStatusEnum from '@/enums/ReferralStatusEnum'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import PracticePlanEnum from '@/enums/PracticePlanEnum'
    import ReferralCloseReasonEnum from '@/enums/ReferralCloseReasonEnum'
    import ReferralsPatientDetailsModal from 'components/page-elements/referrals/ReferralsPatientDetailsModal'
    import ReferralStatusMixin from 'mixins/ReferralStatusMixin'
    import ReferralConfirmMixin from 'mixins/ReferralConfirmMixin'
    import PendingInsuranceAuthorizationModal from 'components/page-elements/referrals-show-pending/PendingInsuranceAuthorizationModal'
    import ReferralsIsPatientSeenModal from 'components/page-elements/ReferralsIsPatientSeenModal'
    import ReferralsAPI from 'api/ReferralsAPI'
    import ReferralsPoolAPI from 'api/ReferralsPoolAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import RequestReportModal from 'components/page-elements/referrals/RequestConsultationReportModal'
    import dayjs from 'dayjs'
    import ReferralPathTypeEnum from '@/enums/ReferralPathTypeEnum'
    import ReferralTrackingTypeEnum from '@/enums/ReferralTrackingTypeEnum'
    import IsReportUploadedModal from './IsReportUploadedModal.vue'
    import AvailableLocationsModal from 'components/page-elements/referrals/AvailableLocationsModal.vue'
    import SubscribeToPremiumModal from './SubscribeToPremiumModal.vue'
    export default {
        mixins: [ReferralStatusMixin, ReferralConfirmMixin, ReferralStatusMixin],

        components: {
            ReferralsPatientDetailsModal,
            PendingInsuranceAuthorizationModal,
            ReferralsIsPatientSeenModal,
            RequestReportModal,
            IsReportUploadedModal,
            AvailableLocationsModal,
            SubscribeToPremiumModal,
        },

        props: {
            isOpen: { type: Boolean, required: true },
            referral: { type: Object, required: true },
            sourceType: { type: String, required: true },
            type: { type: String, required: true },
            isFetching: { type: Boolean, required: true },
        },

        data() {
            return {
                ReferralPathTypeEnum,
                ReferralTrackingTypeEnum,
                ReferralStatusEnum,
                showPatientDetailsModal: false,
                isJustCancelled: false,
                showReferralDetails: false,
                showInsuranceModal: false,
                showPatientSeenModal: false,
                tempReferral: null,
                showRequestReportModal: false,
                reportNewRequestDateTime: null,
                showPatientReviewModal: false,
                showPeerReviewModal: false,
                isProcessing: false,
                isLoading: false,
                isReportUploadedModal: false,
                showAvailableLocationsModal: false,
                showPremiumSubscriptionModal: false,
            }
        },

        computed: {
            referralRoute() {
                return this.latestReferral.consultationReports && this.latestReferral.consultationReports.length > 0
                    ? {
                          name: `referrals.${this.type}.report`,
                          params: { id: this.latestReferral.id },
                      }
                    : {
                          name: 'patients.all-details.patients',
                          params: { id: this.latestReferral.patient.id },
                      }
            },
            isConsultationNotesPendingReceived() {
                return (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.RECEIVED
                )
            },

            isConsultationNotesPendingMade() {
                return (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.MADE
                )
            },
            patientRatingText() {
                return this.showReceivedPatientRating
                    ? this.$t('pages.referrals.show.view_details.rating_for_received', {
                          name: this.toFullName,
                      })
                    : this.$t('pages.referrals.show.view_details.rating_for_new')
            },

            physicianRatingText() {
                return this.showReceivedPhysicianRating
                    ? this.$t('pages.referrals.show.view_details.my_rating_for_received', {
                          name: this.toFullName,
                      })
                    : this.$t('pages.referrals.show.view_details.my_rating_for_new')
            },
            isReceivedReferral() {
                return this.type === ReferralPathTypeEnum.RECEIVED && !this.latestReferral?.isFromPatient
            },

            isMadeDirectReferral() {
                return this.type === ReferralPathTypeEnum.MADE && this.sourceType === ReferralTrackingTypeEnum.DIRECT
            },

            providerSectionType() {
                if (this.isReceivedReferral) return ReferralPathTypeEnum.RECEIVED
                if (this.isMadeDirectReferral) return ReferralPathTypeEnum.MADE
                return null
            },

            providerUser() {
                if (this.providerSectionType === ReferralPathTypeEnum.RECEIVED) {
                    return this.latestReferral?.fromUser || this.latestReferral?.fromPractice
                }
                if (this.providerSectionType === ReferralPathTypeEnum.MADE) {
                    return this.latestReferral?.toUser || this.latestReferral?.practice
                }
                return null
            },

            sectionTitle() {
                if (this.providerSectionType === ReferralPathTypeEnum.RECEIVED) {
                    return this.$t('pages.referrals.show.reschedule_referral_new_modal.roles.referring')
                }
                if (this.providerSectionType === ReferralPathTypeEnum.MADE) {
                    return this.$t('pages.referrals.show.reschedule_referral_new_modal.roles.referred')
                }
                return ''
            },

            specialityUser() {
                if (this.providerSectionType === ReferralPathTypeEnum.RECEIVED) {
                    return this.latestReferral?.fromUser
                }
                if (this.providerSectionType === ReferralPathTypeEnum.MADE) {
                    return this.latestReferral?.toUser
                }
                return null
            },

            referralTypeClass() {
                return (sourceType) => ({
                    'referral-tracking-modal__referrals': true,
                    blue: sourceType === ReferralTrackingTypeEnum.DIRECT,
                    purple: sourceType === ReferralTrackingTypeEnum.MATCH,
                })
            },
            step2Class() {
                return {
                    item: true,
                    done: this.isApproved,
                }
            },
            step4Class() {
                return {
                    item: true,
                    done: this.patientSeenStatus === 'done',
                    crossed: this.patientSeenStatus === 'error',
                }
            },
            step5Class() {
                return {
                    item: true,
                    done: this.referralStatus === 'done',
                    pending: this.referralStatus === 'pending',
                }
            },
            step6Class() {
                return {
                    item: true,
                    done: this.peerRatingStatus === 'done',
                    pending: this.peerRatingStatus === 'pending',
                }
            },
            step7Class() {
                return {
                    item: true,
                    done: this.step7Status === 'done',
                    pending: this.step7Status === 'pending',
                }
            },
            isToUserSubscriptionNetwork() {
                return this.$auth.getPractice().subscription === PracticePlanEnum.NETWORK
            },
            createdAt() {
                const date = this.latestReferral.createdDateTime || this.latestReferral.createdAt
                return date ? dayjs(date).format('MMMM D, YYYY') : ''
            },
            fullDoctorName() {
                return this.latestReferral.fromUser?.firstName + ' ' + this.latestReferral.fromUser?.lastName
            },
            isApproved() {
                const date = this.latestReferral.approveDateTime
                return !!date && dayjs(date).isValid()
            },
            approvedAt() {
                const date = this.latestReferral.approveDateTime
                return date ? dayjs(date).format('MMMM D, YYYY') : ''
            },
            notApprovedText() {
                if (this.latestReferral.status === ReferralStatusEnum.DECLINED) {
                    return this.$t(`pages.referrals.tracking.status_title.DECLINED`)
                } else if (this.latestReferral.closeReason === ReferralCloseReasonEnum.UNANSWERED) {
                    return this.$t('pages.referrals.show.details.cancelled_by_system')
                } else {
                    return this.$t(`pages.referrals.tracking.status_title.CANCELLED`)
                }
            },
            scheduledForDate() {
                const date = this.latestReferral.appointmentDateTime
                return date ? dayjs(date).format('MMMM D, YYYY') : ''
            },

            scheduledForTime() {
                const date = this.latestReferral.appointmentDateTime
                return date ? dayjs(date).format('hh:mm a') : ''
            },

            isInsuranceAuthObtained() {
                let flag
                if (!this.latestReferral.isInsuranceAuthorizationRequired) {
                    flag = true
                } else if (this.latestReferral.status === ReferralStatusEnum.INSURANCE_AUTH_PENDING) {
                    flag = false
                } else {
                    flag = dayjs(this.latestReferral.insuranceAuthorizationObtainedDateTime).isValid()
                }
                return flag
            },

            insuranceAuthObtainedAt() {
                if (this.latestReferral.isInsuranceAuthorizationRequired) {
                    const d = dayjs(this.latestReferral.insuranceAuthorizationObtainedDateTime)
                    return d.isValid() ? d.format('MMMM D, YYYY') : ''
                } else {
                    return this.createdAt
                }
            },
            isInsuranceAuthDeclined() {
                return (
                    this.latestReferral.status === ReferralStatusEnum.CANCELLED &&
                    this.latestReferral.closeReason === ReferralCloseReasonEnum.INSURANCE_AUTH_DENIED
                )
            },
            insuranceStatus() {
                if (!this.isApproved) return 'initial'
                if (this.isInsuranceAuthObtained) return 'done'
                if (this.isInsuranceAuthDeclined) return 'declined'
                return 'pending'
            },

            insuranceTimelineText() {
                const map = {
                    initial: 'pages.referrals.show.details.insurance_authorization_status',
                    done: 'pages.referrals.show.details.insurance_authorization_obtained',
                    declined: 'pages.referrals.show.details.insurance_authorization_declined',
                    pending: 'pages.referrals.show.details.insurance_authorization_pending',
                }

                return this.$t(map[this.insuranceStatus] || map.initial)
            },
            showInsuranceUpdateButton() {
                return (
                    this.latestReferral.state !== 'CLOSED' &&
                    !this.isAppointmentInPast(this.latestReferral) &&
                    this.latestReferral.status !== 'INACTIVE' &&
                    !['done', 'declined'].includes(this.insuranceStatus)
                )
            },
            isReferralTerminatedAfterApproval() {
                return (
                    this.isApproved &&
                    (this.latestReferral.status === ReferralStatusEnum.NO_SHOW ||
                        (this.latestReferral.status === ReferralStatusEnum.CANCELLED &&
                            [
                                ReferralCloseReasonEnum.REFERRED_CANCELLED,
                                ReferralCloseReasonEnum.REFERRING_CANCELLED,
                                ReferralCloseReasonEnum.UNANSWERED,
                            ].includes(this.latestReferral.closeReason)))
                )
            },
            cancelOrNoShowText() {
                if (this.latestReferral.status === ReferralStatusEnum.NO_SHOW) {
                    return this.$t('pages.referrals.show.details.no_show')
                }
                const reason = this.latestReferral.closeReason
                if (reason === ReferralCloseReasonEnum.UNANSWERED) {
                    return this.$t('pages.referrals.show.details.cancelled_by_system')
                }

                let name
                if (reason === ReferralCloseReasonEnum.REFERRED_CANCELLED) {
                    name = this.toFullName
                } else {
                    name = this.fullDoctorName
                }
                return this.$t('pages.referrals.show.details.appointment_cancelled', { name })
            },
            isReferringPhysician() {
                return this.type === ReferralPathTypeEnum.MADE
            },
            showUpdateStatus() {
                return (
                    (this.latestReferral.status === 'PATIENT_VISIT_PENDING' ||
                        this.latestReferral.status === 'INSURANCE_AUTH_PENDING') &&
                    this.isAppointmentInPast(this.latestReferral) &&
                    !this.isReferringPhysician
                )
            },
            latestReferral() {
                return this.tempReferral || this.referral
            },

            patientSeenStatus() {
                if (this.isReferralTerminatedAfterApproval) return 'error'
                if (this.latestReferral.status === 'CONSULTATION_NOTES_PENDING' || this.isReferralCompleted(this.latestReferral))
                    return 'done'
                return 'pending'
            },

            patientSeenTitle() {
                if (this.patientSeenStatus === 'error') return this.cancelOrNoShowText
                if (this.patientSeenStatus === 'done') {
                    return this.$t('pages.referrals.show.details.patient_seen')
                }
                return this.$t('pages.referrals.show.details.patient_visit')
            },

            referralStatus() {
                if (this.latestReferral.status === ReferralStatusEnum.COMPLETED) return 'done'
                if (this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING) return 'pending'
                return ''
            },

            referralStatusTitle() {
                if (this.latestReferral.status === ReferralStatusEnum.COMPLETED)
                    return this.$t('pages.referrals.show.view_details.notes_uploaded')
                if (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.RECEIVED
                )
                    return this.$t('pages.referrals.show.view_details.pending_consultation_reports')
                if (
                    this.latestReferral.status === ReferralStatusEnum.CONSULTATION_NOTES_PENDING &&
                    this.type === ReferralPathTypeEnum.MADE
                )
                    return this.$t('pages.referrals.show.view_details.consultation_report_pending')

                return this.$t('pages.referrals.show.view_details.consultation_notes')
            },

            notesUploadedDate() {
                return dayjs(this.latestReferral.consultationNoteDateTime).format('MMMM D, YYYY')
            },

            referralReportRequestedDateTime() {
                const localDateTime = this.getLocalDateTime(
                    this.reportNewRequestDateTime || this.latestReferral.notesRequestDateTime,
                )
                return dayjs(localDateTime).format('MMMM D, YYYY')
            },

            peerRatingDate() {
                return dayjs(this.latestReferral.physicianReview.createdDateTime).format('MMMM D, YYYY')
            },

            peerRating() {
                if (this.latestReferral.physicianReview?.ratings) {
                    let keys = this.setKeysForPhysicianRating()
                    return this.calculateRating(this.latestReferral.physicianReview?.ratings, keys)
                }

                return null
            },
            peerRatingStatus() {
                if (
                    this.isReferralCompleted(this.latestReferral) &&
                    (this.latestReferral.physicianReview?.ratings || this.latestReferral.patientReview)
                ) {
                    return 'done'
                }
                return 'pending'
            },
            step7Status() {
                if (this.isReferralCompleted(this.latestReferral) && this.latestReferral.physicianReview?.ratings) {
                    return 'done'
                }
                return 'pending'
            },
            proposeSlotsCondition() {
                return (
                    this.type === ReferralPathTypeEnum.RECEIVED &&
                    this.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    [ReferralStatusEnum.CREATED, ReferralStatusEnum.SCHEDULED, ReferralStatusEnum.INACTIVE].includes(
                        this.latestReferral?.status,
                    )
                )
            },
            acceptSlotsConditon() {
                return (
                    this.type === ReferralPathTypeEnum.MADE &&
                    this.sourceType === ReferralTrackingTypeEnum.MATCH &&
                    [ReferralStatusEnum.TIME_SLOTS_PROPOSED, ReferralStatusEnum.SCHEDULED, ReferralStatusEnum.INACTIVE].includes(
                        this.latestReferral?.status,
                    )
                )
            },
            showTimelineCondition() {
                return (
                    this.sourceType === ReferralTrackingTypeEnum.DIRECT &&
                    [ReferralPathTypeEnum.RECEIVED, ReferralPathTypeEnum.MADE].includes(this.type)
                )
            },
            toFullName() {
                if (this.latestReferral.practice?.id) {
                    return this.latestReferral.practice.name
                } else {
                    return `${this.latestReferral.toUser.firstName} ${this.latestReferral.toUser.lastName}`
                }
            },
            referralStatusClass() {
                if (this.latestReferral.status === 'SCHEDULED') return 'done'
                if (this.latestReferral.status === 'INACTIVE') return 'crossed'
                return ''
            },
            shouldShowRightContent() {
                return this.shouldShowReferralDetails(this.latestReferral) && !this.$auth.isUserPracticeOther() && this.isApproved
            },
            showPatientSeenPending() {
                return (
                    this.patientSeenStatus === 'pending' &&
                    this.isAppointmentInPast(this.latestReferral) &&
                    !this.isReferringPhysician
                )
            },
            showReceivedPatientRating() {
                return this.isReferralCompleted(this.latestReferral) && this.latestReferral.patientReview
            },

            showReceivedPhysicianRating() {
                return this.isReferralCompleted(this.latestReferral) && this.latestReferral.physicianReview?.ratings
            },

            showPendingReviewBlock() {
                return (
                    this.isReferralCompleted(this.latestReferral) &&
                    !this.latestReferral.physicianReview?.ratings &&
                    this.latestReferral.patientReview
                )
            },

            isStatusCompletedAndUserNotOther() {
                return this.isReferralCompleted(this.latestReferral) && !this.$auth.isUserPracticeOther()
            },
        },

        methods: {
            getUserFullName(patient) {
                return patient ? `${patient.firstName} ${patient.lastName}` : 'N/A'
            },
            getPatientDateOfBirth(dob) {
                if (!dob) return 'N/A'

                const [year, month, day] = dob.split('-')
                return `${month}-${day}-${year}`
            },
            getUserName(referral) {
                if (!referral) return 'N/A'

                const practiceType = this.$auth.getPractice()?.practiceType
                const isMade = this.activeReferralTab === ReferralPathTypeEnum.MADE

                const user = isMade ? referral?.toUser : referral?.fromUser
                const practice = isMade
                    ? referral?.practice || referral?.toPractice || referral?.fromPractice
                    : referral?.fromPractice || referral?.fromUser

                if (practiceType === PracticeTypeEnum.PHYSICIAN && user) {
                    const designation = user.designation ? user.designation + ' ' : ''
                    const firstName = user.firstName || ''
                    const lastName = user.lastName || ''

                    const fullName = `${designation}${firstName} ${lastName}`.trim()
                    if (fullName) return fullName
                }

                if (practiceType === PracticeTypeEnum.ANCILLARY) {
                    if (practice?.name) return practice.name
                    if (practice?.firstName) {
                        const designation = user.designation ? user.designation + ' ' : ''
                        const firstName = user.firstName || ''
                        const lastName = user.lastName || ''

                        const fullName = `${designation}${firstName} ${lastName}`.trim()
                        return fullName
                    }
                }

                if (practice?.name) return practice.name

                return 'N/A'
            },
            getUserSpeciality(user) {
                if (!user || !user.specialities || user.specialities.length === 0) {
                    return ''
                }

                return user.specialities[0]?.speciality?.name || ''
            },
            getReferralType(type) {
                if (type === ReferralTrackingTypeEnum.DIRECT) return this.$t('pages.referrals.index.referrals_made.type_direct')
                if (type === ReferralTrackingTypeEnum.MATCH) return this.$t('pages.referrals.index.referrals_made.type_matched')
                return 'N/A'
            },
            getStageClass(stage) {
                if (!stage) return 'referral-tracking__referrals gray'

                switch (stage) {
                    case StagesEnum.CONFIRMED:
                    case StagesEnum.AWAITING_PATIENT_VISIT:
                        return 'referral-tracking__referrals green'

                    case StagesEnum.REFERRAL_SUBMITTED:
                    case StagesEnum.AWAITING_PROVIDER_PROPOSALS:
                    case StagesEnum.AWAITING_REFERRER_SELECTION:
                    case StagesEnum.PENDING_PROVIDER_CONFIRMATION:
                        return 'referral-tracking__referrals yellow'

                    case StagesEnum.PROPOSALS_CLOSED:
                        return 'referral-tracking__referrals purple'

                    case StagesEnum.SELECT_TIME_SLOT:
                        return 'referral-tracking__referrals blue'

                    case StagesEnum.CANCELLED_NO_PROPOSALS:
                    case StagesEnum.CANCELLED_NO_SELECTION:
                        return 'referral-tracking__referrals today'

                    case StagesEnum.PROPOSE_TIME_SLOTS:
                        return 'referral-tracking__referrals purple'

                    default:
                        return 'referral-tracking__referrals gray'
                }
            },
            formatStage(stage) {
                if (!stage) return 'N/A'

                return stage
                    .toLowerCase()
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
            },
            getCreatedDate(latestReferral) {
                const dateValue = latestReferral?.createdDateTime || latestReferral?.createdAt

                if (!dateValue) return ''

                return new Date(dateValue).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })
            },
            getCreatedDateTime(latestReferral) {
                const date = new Date(latestReferral.createdDateTime)

                const formattedDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })

                const formattedTime = date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                })

                return `${formattedDate} at ${formattedTime}`
            },
            getAppointmentDateTime(latestReferral) {
                const date = new Date(latestReferral.appointmentDateTime)

                const formattedDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                })

                const formattedTime = date.toLocaleTimeString('en-US', {
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true,
                })

                return `${formattedDate} at ${formattedTime}`
            },
            showProviderSection() {
                const isPatientReferralRoute = this.$route.path.includes('/referrals-patient')
                if (isPatientReferralRoute) {
                    return false
                }
                return (
                    this.type === ReferralPathTypeEnum.RECEIVED ||
                    (this.type === ReferralPathTypeEnum.MADE && this.sourceType === ReferralTrackingTypeEnum.DIRECT)
                )
            },
            openReferralInfoModal() {
                this.showPatientDetailsModal = true
            },
            openInsuranceAuthorizationModal() {
                this.showInsuranceModal = true
            },
            closeInsuranceAuthorizationModal() {
                this.showInsuranceModal = false
            },
            openPatientSeenModal() {
                this.showPatientSeenModal = true
            },
            patientSeen(event) {
                if (this.$auth.isUserPracticeDemo()) {
                    return
                }
                ReferralsAPI.updatePatientVisitStatus(this.latestReferral.id, event.status).then((ev) => {
                    this.showPatientSeenModal = false
                    this.latestReferral.status = ev.status
                    this.latestReferral.insuranceAuthorizationObtainedDateTime = ev.insuranceAuthorizationObtainedDateTime
                    if (event.status === 'patient-seen') {
                        this.$router.push({
                            name: `referrals.received.add-report`,
                            params: {
                                id: this.latestReferral.id,
                            },
                        })
                    }
                })
            },
            reportAlreadyAdded() {
                this.isLoading = true
                ReferralsAPI.sendConsultationReport(this.latestReferral.id, true).then(() => {
                    this.refreshReferralDetails()
                })
            },
            addNotesClicked() {
                this.$router.push({
                    name: `referrals.received.add-report`,
                    params: {
                        id: this.latestReferral.id,
                    },
                })
            },

            openRequestReportModal() {
                this.showRequestReportModal = true
            },

            closeRequestReportModal() {
                this.showRequestReportModal = false
            },

            requestReportSent() {
                this.reportNewRequestDateTime = dayjs().format()
                this.$refs.commonSuccessNotificationChild.showNotification()
                this.closeRequestReportModal()
            },

            viewPeerReviewDetailsClicked() {
                this.showPeerReviewModal = true
            },

            viewPeerReviewDetailsModalClosed() {
                this.showPeerReviewModal = false
            },

            processReferral() {
                if (!this.$auth.isPracticePremium()) {
                    this.showPremiumSubscriptionModal = true
                    return
                }
                this.isProcessing = true
                PracticesAPI.acceptInsuranceAndTests(
                    this.$auth.getPractice().id,
                    [this.latestReferral.insurancePlanId],
                    this.latestReferral.referralTests,
                ).then(() => {
                    const query = {
                        address: this.latestReferral.searchBy?.address || undefined,
                        city: this.latestReferral.searchBy?.city || undefined,
                        state: this.latestReferral.searchBy?.state || undefined,
                        zip: this.latestReferral.searchBy?.zip || undefined,
                        patientId: this.latestReferral.patientId,
                        referralPoolId: this.latestReferral.id,
                    }
                    const request = this.$auth.isPracticeAncillary()
                        ? ReferralsPoolAPI.getAncillaryAvailableLocations(this.$auth.getPractice().id, query)
                        : ReferralsPoolAPI.getAvailableLocations(this.$auth.getActiveMember().id, query)

                    request
                        .then((response) => {
                            this.locations = response
                            if (this.latestReferral?.toPracticeLocationId) {
                                this.goToTimeProposal({ id: this.latestReferral.toPracticeLocationId })
                                return
                            }

                            if (this.locations.length > 1) {
                                this.showAvailableLocationsModal = true
                            } else {
                                if (this.$auth.getPractice().practiceType === PracticeTypeEnum.ANCILLARY) {
                                    this.goToTimeProposal({ id: 0 })
                                } else {
                                    this.goToTimeProposal(this.locations[0])
                                }
                            }
                        })
                        .finally(() => {
                            this.isProcessing = false
                        })
                })
            },
            closeAvailableLocationsModal() {
                this.showAvailableLocationsModal = false
            },

            goToTimeProposal(event) {
                this.$router.push({
                    name: `referrals.pool.propose.time`,
                    params: {
                        poolId: this.latestReferral.id,
                        locationId: event.id,
                    },
                    query: {
                        physicianId: this.$auth.getActiveMember()?.id
                            ? this.$auth.getActiveMember()?.id
                            : this.$auth.getUser().id,
                        practiceId: this.$auth.getPractice().id,
                        userId: this.getUserId(),
                        practiceLocationId: event.id,
                        fromUserId: this.latestReferral?.fromUser?.id,
                        timeFrame: this.latestReferral?.timeframe,
                        fromDate:
                            this.latestReferral?.timeframe === 'custom' && this.latestReferral?.customDate
                                ? dayjs(this.latestReferral.customDate).format('YYYY-MM-DD')
                                : dayjs(this.latestReferral?.createdAt).format('YYYY-MM-DD'),
                        distance: event.distance ?? undefined,
                        practiceType: this.$auth.getPractice().practiceType,
                        isFromPatient: this.latestReferral?.isFromPatient,
                        patientId: this.latestReferral?.patientId,
                    },
                })
            },
            getUserId() {
                if (this.$auth.getPractice().practiceType !== PracticeTypeEnum.ANCILLARY) {
                    return this.$auth.getActiveMember()?.id ? this.$auth.getActiveMember()?.id : this.$auth.getUser().id
                }
            },
            refreshReferralDetails() {
                this.isLoading = true
                const userId = this.$auth.getActiveMember()?.id || this.$auth.getUser().id
                if (this.sourceType === ReferralTrackingTypeEnum.DIRECT) {
                    ReferralsAPI.get(this.latestReferral.id, userId)
                        .then((res) => {
                            this.tempReferral = res
                        })
                        .finally(() => (this.isLoading = false))
                } else {
                    ReferralsPoolAPI.getReferralPoolById(this.latestReferral.id, this.type, this.$auth.getActiveMember()?.id)
                        .then((res) => {
                            this.tempReferral = res
                        })
                        .finally(() => (this.isLoading = false))
                }
            },
            declineReferral() {
                ReferralsPoolAPI.decline(this.latestReferral.id, {
                    referralPoolId: this.latestReferral.id,
                    userId: this.$auth.getActiveMember()?.id || this.$auth.getUser().id,
                    practiceId: this.$auth.getPractice()?.id,
                }).then(() => {
                    this.refreshReferralDetails()
                    this.emitEvent({
                        name: 'fetch-referral',
                        payload: this.latestReferral.id,
                    })
                })
            },
        },
    }
</script>
